import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import flow from 'lodash/flow';
import memoize from 'memoize-one';

import {
  getPipeQueueSize,
  isInputEndpointPipe,
  getSourceSystemId,
  isOutputEndpointPipe,
  getSinkSystemId,
} from '../internals/pipes';

import type { Path } from '../internals/graph';
import type { PipeMap } from '../types/pipes.types';
import type { Flow, FlowInputOutput, HoverNodeParams } from '../types/flows.types';

const getSumQueues = (path: string, pipes: PipeMap) => {
  let sum = 0;

  for (const id of path) {
    sum = sum + getPipeQueueSize(pipes[id]);
  }

  return sum;
};

export const getSumQueuesMemoized = memoize(getSumQueues);

const getFlowLastRun = (path: Path, pipes: PipeMap) => {
  const pipesRunStatuses = path.map((id) => {
    const pipe = pipes[id];

    if (pipe) {
      return get(pipe, 'runtime.success');
    } else {
      return null;
    }
  });

  if (pipesRunStatuses.includes(null)) {
    return null;
  }

  if (pipesRunStatuses.includes(false)) {
    return false;
  }

  return true;
};

export const getFlowLastRunMemo = memoize(getFlowLastRun);

export const resolveNodeId = (idOrParams: string | HoverNodeParams) =>
  typeof idOrParams === 'object' && idOrParams !== null ? idOrParams.node : idOrParams;

export const findFlowNodeIndex = (
  idOrParams: string | HoverNodeParams,
  flow: FlowInputOutput[]
): number => {
  const id = resolveNodeId(idOrParams);
  let skip = 0;
  let name: string;
  if (id.indexOf('::loop') !== -1) {
    [name, skip] = id.split('::loop') as [string, number];
  } else {
    name = id;
  }

  const idx = flow.findIndex((c) => {
    if (c.data._id === name && skip === 0) {
      return true;
    } else if (c.data._id === name && skip > 0) {
      skip--;
    }
    return false;
  });

  return idx < 0 ? 0 : idx;
};

export const HOP_IDENTIFIER = '---';

const resolvePathToNodes = (path: string[], pipes: PipeMap): (FlowInputOutput | undefined)[] => {
  let isHop = false;

  return path
    .map((id) => {
      if (id === HOP_IDENTIFIER) {
        isHop = true;
        return HOP_IDENTIFIER;
      }

      const pipe = pipes[id];

      if (!pipe) {
        return undefined;
      }

      const result: FlowInputOutput = { data: pipe, isHop };

      isHop = false;
      return result;
    })
    .filter((c) => c !== HOP_IDENTIFIER) as (FlowInputOutput | undefined)[];
};

const prependSourceSystem =
  (systems: Record<string, any>) =>
  (nodes: FlowInputOutput[]): FlowInputOutput[] => {
    if (!isInputEndpointPipe(nodes[0].data)) {
      return nodes;
    }

    const srcId = getSourceSystemId(nodes[0].data);
    const system = srcId ? systems[srcId] : undefined;

    if (!system) {
      return nodes;
    }

    return [{ data: system, isHop: false }, ...nodes];
  };

const appendSinkSystem =
  (systems: Record<string, any>) =>
  (nodes: FlowInputOutput[]): FlowInputOutput[] => {
    if (!isOutputEndpointPipe(nodes[nodes.length - 1].data)) {
      return nodes;
    }

    const sinkId = getSinkSystemId(nodes[nodes.length - 1].data);
    const system = sinkId ? systems[sinkId] : undefined;

    if (!system) {
      return nodes;
    }

    return [...nodes, { data: system, isHop: false }];
  };

export const getFlow = (
  path: string | string[] | undefined,
  pipes: PipeMap,
  systems: Record<string, any>
): FlowInputOutput[] | null | undefined => {
  if (!path) {
    return null;
  }

  if (isEmpty(pipes)) {
    return undefined;
  }

  const pathArray = Array.isArray(path) ? path : [path];
  const nodes = resolvePathToNodes(pathArray, pipes);

  if (nodes.length === 0 || nodes.includes(undefined)) {
    return null;
  }

  const resolved = nodes as FlowInputOutput[];

  return flow([prependSourceSystem(systems), appendSinkSystem(systems)])(resolved);
};

export const getFlows = (flows: Flow[], pipes: PipeMap) => {
  const finalFlows = flows.map((flow) => {
    const status = getFlowLastRunMemo(flow.path, pipes);

    if (status === null) {
      return { ...flow, lastRunStatus: 'Unknown' as const, lastRunStatusRaw: null };
    }

    if (status) {
      return { ...flow, lastRunStatus: 'Success' as const, lastRunStatusRaw: true };
    } else {
      return { ...flow, lastRunStatus: 'Failure' as const, lastRunStatusRaw: false };
    }
  });

  return finalFlows;
};
